import React, { useState, useRef, useEffect } from "react";
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Button,
    TextField,
    Dialog,
    DialogContent,
    DialogTitle,
    Alert,
    AlertTitle,
    Box,
    Typography,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
    Mail,
    Phone,
    Shield,
    Edit,
    Check,
    Close,
    Cake,
    WcOutlined,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import API_ENDPOINTS from "../../apis/endpoints";
import useAuth from "../../context";

const StyledCard = styled(Card)(({ theme }) => ({
    maxWidth: 600,
    margin: "auto",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
    transition: "transform 0.3s ease-in-out",
    "&:hover": {
        transform: "translateY(-5px)",
    },
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(12),
    height: theme.spacing(12),
    border: `4px solid ${theme.palette.background.paper}`,
    boxShadow: "0 0 0 4px rgba(255, 255, 255, 0.2)",
}));

const InfoCard = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    marginBottom: theme.spacing(2),
    transition: "background-color 0.3s ease",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
    height: "6rem",
}));

const OtpInput = styled(TextField)({
    width: "40px",
    margin: "0 4px",
    "& input": {
        textAlign: "center",
        fontSize: "1.2rem",
    },
});

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 2,
    textTransform: "none",
    fontWeight: 600,
}));

const UserProfile = () => {
    const auth = useAuth();
    const [user, setUser] = useState({
        first_name: auth.user.first_name,
        last_name: auth.user.last_name,
        email: auth.user.email,
        phone_number: auth.user.phone_number,
        role: auth.user.role,
        pic: auth.user.picture,
        gender: auth.user.gender || "",
        birthdate: auth.user.birthdate || null,
    });

    const [isEditing, setIsEditing] = useState({
        name: false,
        phone: false,
        gender: false,
        birthdate: false,
    });
    const ErrorText = styled("p")(({ theme }) => ({
        color: theme.palette.error.main,
        fontSize: "0.875rem",
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    }));

    const [newFirstName, setNewFirstName] = useState(user.first_name);
    const [newLastName, setNewLastName] = useState(user.last_name);
    const [newGender, setNewGender] = useState(user.gender);
    const [newBirthdate, setNewBirthdate] = useState(user.birthdate);

    const [showOtpDialog, setShowOtpDialog] = useState(false);
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [timer, setTimer] = useState(30);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [newPhone, setNewPhone] = useState("");
    const [error, setError] = useState("");
    const [deviceToken, setDeviceToken] = useState("");

    const otpRefs = useRef([]);
    const phoneRegex = /^[6-9]\d{9}$/;

    const handleSendVerificationCode = async () => {
        setError(false);

        try {
            let formData = new FormData();
            formData.append("phone_number", newPhone);
            formData.append("type", "send");

            const response = await fetch(`${API_ENDPOINTS.USER}phone/`, {
                method: "POST",
                headers: {
                    // 'Content-Type': 'application/json',
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": auth.deviceId,
                },
                body: formData,
            });
            const data = await response.json();

            if (data.success) {
                setDeviceToken(data.data.token);
                // setVerificationToken(data.data.token);
                // setAttemptsLeft(data.data.attempts_left);
                // setConfirmNumber(`${countryCode}${phoneNumber}`);
                // You might want to start a timer here based on data.data.expires
            } else {
                setError(data?.message);
            }
        } catch (error) {
            console.error("Error sending verification code:", error);
            setError("Error sending verification code");
        }
    };

    const handleVerifyCode = async () => {
        setError(false);

        const enteredOtp = otp.join("");
        if (enteredOtp.length === 6) {
            setUser((prev) => ({ ...prev, phone_number: newPhone }));
            setShowOtpDialog(false);
            setIsEditing((prev) => ({ ...prev, phone: false }));
            setOtp(["", "", "", "", "", ""]);
        }
        try {
            let formData = new FormData();
            formData.append("code", enteredOtp);
            formData.append("token", deviceToken);
            formData.append("type", "verify");

            const response = await fetch(`${API_ENDPOINTS.USER}phone/`, {
                method: "POST",
                headers: {
                    // 'Content-Type': 'application/json',
                    Authorization: `Bearer ${auth.accessToken}`,

                    "x-cc-device-id": auth.deviceId,
                },
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                setShowOtpDialog(true);
                setIsTimerActive(true);
                setTimer(30);
            } else {
                setError(data?.message);
            }
        } catch (error) {
            console.error("Error verifying code:", error);
            setError("Error verifying code");
        }
    };

    const handleResendCode = async () => {
        setError(false);

        setTimer(30);
        setIsTimerActive(true);
        setOtp(["", "", "", "", "", ""]);
        try {
            let formData = new FormData();
            formData.append("type", "phone-resend");

            const response = await fetch(`${API_ENDPOINTS.LOGIN}`, {
                method: "POST",
                headers: {
                    // 'Content-Type': 'application/json',
                    Authorization: `Bearer ${auth.accessToken}`,

                    "x-cc-device-id": auth.deviceId,
                },
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                // You might want to start a timer here based on data.data.expires
            } else {
                setError(data?.message);
            }
        } catch (error) {
            console.error("Error resending code:", error);
            setError("Error resending code");
        }
    };

    useEffect(() => {
        let interval = null;
        if (isTimerActive && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setIsTimerActive(false);
        }
        return () => clearInterval(interval);
    }, [isTimerActive, timer]);

    const handleEdit = (field) => {
        setIsEditing((prev) => ({ ...prev, [field]: true }));
    };

    const handleCancel = (field) => {
        setIsEditing((prev) => ({ ...prev, [field]: false }));
        if (field === "name") {
            setNewFirstName(user.first_name);
            setNewLastName(user.last_name);
        } else if (field === "phone") {
            setNewPhone(user.phone_number);
        } else if (field === "gender") {
            setNewGender(user.gender);
        } else if (field === "birthdate") {
            setNewBirthdate(user.birthdate);
        }
    };

    const handleSave = async (field) => {
        setError(false);
        if (field === "name") {
            setUser((prev) => ({
                ...prev,
                first_name: newFirstName,
                last_name: newLastName,
            }));
        } else if (field === "gender") {
            setUser((prev) => ({ ...prev, gender: newGender }));
        } else if (field === "birthdate") {
            setUser((prev) => ({ ...prev, birthdate: newBirthdate }));
        }
        setIsEditing((prev) => ({ ...prev, [field]: false }));

        // Create FormData object for API submission
        const formData = new FormData();
        formData.append("first_name", newFirstName);
        formData.append("last_name", newLastName);
        formData.append("gender", newGender);
        formData.append(
            "birthdate",
            newBirthdate ? new Date(newBirthdate).toLocaleDateString() : null
        );

        // Log the FormData (for demonstration purposes)
        for (let [key, value] of formData.entries()) {
            /* empty */
        }

        const storedDeviceId = localStorage.getItem("device-id");

        try {
            const response = await fetch(`${API_ENDPOINTS.USER}`, {
                method: "PATCH",
                headers: {
                    // "Content-Type": "application/json",
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
                body: formData,
            });
            const userDetails = await response.json();

            auth.setUser(userDetails?.data?.user);
        } catch (error) {
            console.error(error);
        } // For example:
        // sendDataToApi(formData);
    };

    const handlePhoneSubmit = async () => {
        if (phoneRegex.test(newPhone)) {
            await handleSendVerificationCode();
        }
    };

    const handleOtpChange = (index, value) => {
        if (value.length <= 1) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value !== "" && index < 5) {
                otpRefs.current[index + 1].focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpRefs.current[index - 1].focus();
        }
    };

    const handleVerifyOtp = () => {
        const enteredOtp = otp.join("");
        if (enteredOtp.length === 6) {
            setUser((prev) => ({ ...prev, phone_number: newPhone }));
            setShowOtpDialog(false);
            setIsEditing((prev) => ({ ...prev, phone: false }));
            setOtp(["", "", "", "", "", ""]);
        }
    };

    return (
        <Box
            className="profile-container"
            sx={{
                padding: 3,
                backgroundColor: "#f5f5f5",
                minHeight: "100vh",
            }}
        >
            <StyledCard sx={{ width: "70%" }}>
                <StyledCardHeader
                    avatar={
                        <StyledAvatar alt={user.first_name[0]} src={user.pic} />
                    }
                    title={
                        isEditing.name ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <TextField
                                    value={newFirstName}
                                    onChange={(e) =>
                                        setNewFirstName(e.target.value)
                                    }
                                    size="small"
                                    label="First Name"
                                />
                                <TextField
                                    value={newLastName}
                                    onChange={(e) =>
                                        setNewLastName(e.target.value)
                                    }
                                    size="small"
                                    label="Last Name"
                                />
                                <StyledButton
                                    onClick={() => handleSave("name")}
                                    variant="contained"
                                    color="secondary"
                                >
                                    <Check />
                                </StyledButton>
                                <StyledButton
                                    onClick={() => handleCancel("name")}
                                    variant="outlined"
                                    color="secondary"
                                >
                                    <Close />
                                </StyledButton>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Typography variant="h5" component="h1">
                                    {user.first_name} {user.last_name}
                                </Typography>
                                <StyledButton
                                    onClick={() => handleEdit("name")}
                                    variant="outlined"
                                    color="secondary"
                                >
                                    <Edit />
                                </StyledButton>
                            </Box>
                        )
                    }
                    subheader={
                        <Chip
                            icon={<Shield fontSize="small" />}
                            label={user.role}
                            color="secondary"
                            size="small"
                            sx={{ textTransform: "capitalize", mt: 1 }}
                        />
                    }
                />

                <CardContent>
                    <InfoCard>
                        <Mail color="primary" sx={{ mr: 2, fontSize: 30 }} />
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Email
                            </Typography>
                            <Typography variant="body1">
                                {user.email}
                            </Typography>
                        </Box>
                    </InfoCard>

                    <InfoCard>
                        <Phone color="primary" sx={{ mr: 2, fontSize: 30 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Phone
                            </Typography>
                            {isEditing.phone ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <TextField
                                        value={newPhone}
                                        onChange={(e) =>
                                            setNewPhone(e.target.value)
                                        }
                                        placeholder="XXXXXXXXXX"
                                        size="small"
                                        fullWidth
                                    />
                                    <StyledButton
                                        variant="contained"
                                        color="primary"
                                        onClick={handlePhoneSubmit}
                                        disabled={!phoneRegex.test(newPhone)}
                                    >
                                        <Check />
                                    </StyledButton>
                                    <StyledButton
                                        variant="outlined"
                                        onClick={() => handleCancel("phone")}
                                    >
                                        <Close />
                                    </StyledButton>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="body1">
                                        {user.phone_number}
                                    </Typography>
                                    <StyledButton
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleEdit("phone")}
                                    >
                                        <Edit fontSize="small" />
                                    </StyledButton>
                                </Box>
                            )}
                        </Box>
                    </InfoCard>

                    <InfoCard>
                        <Cake color="primary" sx={{ mr: 2, fontSize: 30 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Date of Birth
                            </Typography>
                            {isEditing.birthdate ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <LocalizationProvider
                                        dateAdapter={AdapterDateFns}
                                    >
                                        <DatePicker
                                            value={newBirthdate}
                                            onChange={(newValue) =>
                                                setNewBirthdate(newValue)
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                    <StyledButton
                                        onClick={() => handleSave("birthdate")}
                                        variant="contained"
                                        color="primary"
                                    >
                                        <Check />
                                    </StyledButton>
                                    <StyledButton
                                        onClick={() =>
                                            handleCancel("birthdate")
                                        }
                                        variant="outlined"
                                    >
                                        <Close />
                                    </StyledButton>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="body1">
                                        {user.birthdate
                                            ? new Date(
                                                  user.birthdate
                                              ).toLocaleDateString()
                                            : "Not set"}
                                    </Typography>
                                    <StyledButton
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleEdit("birthdate")}
                                    >
                                        <Edit fontSize="small" />
                                    </StyledButton>
                                </Box>
                            )}
                        </Box>
                    </InfoCard>

                    <InfoCard>
                        <WcOutlined
                            color="primary"
                            sx={{ mr: 2, fontSize: 30 }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Gender
                            </Typography>
                            {isEditing.gender ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        {/* <InputLabel id="gender-select-label">
                                            Gender
                                        </InputLabel> */}
                                        <Select
                                            labelId="gender-select-label"
                                            value={newGender}
                                            // label="Gender"
                                            onChange={(e) =>
                                                setNewGender(e.target.value)
                                            }
                                        >
                                            <MenuItem value="Male">
                                                Male
                                            </MenuItem>
                                            <MenuItem value="Female">
                                                Female
                                            </MenuItem>
                                            <MenuItem value="Other">
                                                Other
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <StyledButton
                                        onClick={() => handleSave("gender")}
                                        variant="contained"
                                        color="primary"
                                    >
                                        <Check />
                                    </StyledButton>
                                    <StyledButton
                                        onClick={() => handleCancel("gender")}
                                        variant="outlined"
                                    >
                                        <Close />
                                    </StyledButton>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="body1">
                                        {user.gender || "Not set"}
                                    </Typography>
                                    <StyledButton
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleEdit("gender")}
                                    >
                                        <Edit fontSize="small" />
                                    </StyledButton>
                                </Box>
                            )}
                        </Box>
                    </InfoCard>
                    {error ? <ErrorText>{error}</ErrorText> : null}
                </CardContent>
            </StyledCard>

            <Dialog
                open={showOtpDialog}
                onClose={() => setShowOtpDialog(false)}
            >
                <DialogTitle>Verify Phone Number</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <AlertTitle>
                            Enter the 6-digit code sent to {newPhone}
                        </AlertTitle>
                    </Alert>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 2,
                        }}
                    >
                        {otp.map((digit, index) => (
                            <OtpInput
                                key={index}
                                inputRef={(el) => (otpRefs.current[index] = el)}
                                value={digit}
                                onChange={(e) =>
                                    handleOtpChange(index, e.target.value)
                                }
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                inputProps={{ maxLength: 1 }}
                            />
                        ))}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <StyledButton
                            variant="text"
                            disabled={isTimerActive}
                            onClick={handleResendCode}
                        >
                            Resend Code {isTimerActive && `(${timer}s)`}
                        </StyledButton>
                        <StyledButton
                            variant="contained"
                            onClick={handleVerifyCode}
                        >
                            Verify
                        </StyledButton>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default UserProfile;
